/**
 * Client-side wallet/tx detection (mirrors nextjs_space/lib/chains.ts).
 * Used to route address → investigate and hash → transaction lookup.
 */

export type WalletChainId =
  | 'btc'
  | 'eth'
  | 'ltc'
  | 'doge'
  | 'dash'
  | 'bch'
  | 'trx'
  | 'sol';

export interface WalletChainConfig {
  id: WalletChainId;
  name: string;
  symbol: string;
  color: string;
}

export const WALLET_CHAINS: Record<WalletChainId, WalletChainConfig> = {
  btc: { id: 'btc', name: 'Bitcoin', symbol: 'BTC', color: '#f7931a' },
  eth: { id: 'eth', name: 'Ethereum', symbol: 'ETH', color: '#627eea' },
  ltc: { id: 'ltc', name: 'Litecoin', symbol: 'LTC', color: '#bfbbbb' },
  doge: { id: 'doge', name: 'Dogecoin', symbol: 'DOGE', color: '#c2a633' },
  dash: { id: 'dash', name: 'Dash', symbol: 'DASH', color: '#008ce7' },
  bch: { id: 'bch', name: 'Bitcoin Cash', symbol: 'BCH', color: '#0ac18e' },
  trx: { id: 'trx', name: 'TRON', symbol: 'TRX', color: '#ff060a' },
  sol: { id: 'sol', name: 'Solana', symbol: 'SOL', color: '#14f195' },
};

export const SUPPORTED_WALLET_CHAINS = Object.values(WALLET_CHAINS);

const BASE58 = 'a-km-zA-HJ-NP-Z1-9';

/**
 * Detect which blockchain an address belongs to based on its format.
 * Returns null if the address doesn't match any supported chain.
 */
export function detectWalletChain(input: string): WalletChainConfig | null {
  const addr = String(input ?? '').trim();
  if (!addr) return null;

  // Ethereum / EVM (0x + 40 hex)
  if (/^0x[a-fA-F0-9]{40}$/.test(addr)) return WALLET_CHAINS.eth;

  // TRON (T + 33 base58)
  if (new RegExp(`^T[${BASE58}]{33}$`).test(addr)) return WALLET_CHAINS.trx;

  // Bech32 prefixes
  if (/^bc1[a-zA-HJ-NP-Z0-9]{25,62}$/i.test(addr)) return WALLET_CHAINS.btc;
  if (/^ltc1[a-zA-HJ-NP-Z0-9]{25,62}$/i.test(addr)) return WALLET_CHAINS.ltc;

  // Bitcoin Cash CashAddr (with or without prefix)
  if (/^(bitcoincash:)?[qp][a-z0-9]{41}$/.test(addr)) return WALLET_CHAINS.bch;

  // Base58 legacy formats
  if (new RegExp(`^[13][${BASE58}]{25,34}$`).test(addr)) return WALLET_CHAINS.btc;
  if (new RegExp(`^[LM][${BASE58}]{25,34}$`).test(addr)) return WALLET_CHAINS.ltc;
  if (new RegExp(`^D[${BASE58}]{25,34}$`).test(addr)) return WALLET_CHAINS.doge;
  if (new RegExp(`^X[${BASE58}]{25,34}$`).test(addr)) return WALLET_CHAINS.dash;

  // Solana (base58 ed25519 pubkey, typically 32-44 chars). Checked last.
  if (new RegExp(`^[${BASE58}]{32,44}$`).test(addr)) return WALLET_CHAINS.sol;

  return null;
}

/**
 * Client-safe check: does the input look like a transaction ID / hash?
 * ETH-style: 0x + 64 hex. BTC/TRON/LTC/DOGE/DASH/BCH: bare 64 hex.
 */
export function looksLikeWalletTxId(input: string): boolean {
  const s = String(input ?? '').trim();
  return /^(0x)?[a-fA-F0-9]{64}$/.test(s);
}
